import type { Client } from 'graphql-ws'
import { createClient } from 'graphql-ws'
import {
  createIssuanceRequestMutation,
  createPhotoCaptureRequestMutation,
  createPresentationRequestForAuthnMutation,
  createPresentationRequestMutation,
  findIssuanceQuery,
  findPresentationQuery,
  issuanceEventSubscription,
  photoCaptureEventSubscription,
  photoCaptureStatusQuery,
  presentationEventSubscription,
} from './operations'
import type {
  Issuance,
  IssuanceResponse,
  PhotoCaptureEventData,
  PhotoCaptureRequestInput,
  PhotoCaptureResponse,
  Presentation,
  PresentationEventData,
  PresentationRequestInput,
  PresentationResponse,
} from './types'
import {
  IssuanceRequestStatus,
  PhotoCaptureStatus,
  PresentationRequestStatus,
  type IssuanceEventData,
  type IssuanceRequestInput,
} from './types'

const defaultEventTimeout = 1000 * 60 * 5
const defaultPollWaitInterval = 1000

export class VerifiedOrchestrationClient {
  constructor({ url, accessToken }: { url: string; accessToken: string }) {
    if (!url || !accessToken) throw new Error('url and accessToken are required')
    this.url = url
    this.accessToken = accessToken
    this.wsClient = createClient({
      url: this.url.replace('http', 'ws'),
      connectionParams: { Authorization: `Bearer ${this.accessToken}` },
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      webSocketImpl: typeof WebSocket !== 'undefined' ? WebSocket : require('ws'),
      retryAttempts: 100, // defaults to randomised exponential backoff
      shouldRetry: () => true,
    })
  }

  url: string
  accessToken: string
  wsClient: Client

  /**
   * Initiates an issuance request using the supplied input.
   * Returns an issuance request response (or throws an error).
   * Issuance events are received via the onIssuanceEvent callback.
   *
   * Issuance has the following steps:
   * #1 An issuance request is created and returned, it contains a QR code and URL, plus the request ID and expiry.
   * #2 For mobile apps, the URL can opened immediately to initiate the issuance process in Microsoft Authenticator. For web apps, the QR code should be displayed to be scanned via a mobile device to initiate issuance in Microsoft Authenticator.
   * #3 The onIssuanceEvent callback will receive issuance events (or an Error). The first event status will be 'issuance_retrieved' when the request has been opened by Microsoft Authenticator. If a PIN code has been specified in the issuance request, it must be entered by the user at this point.
   * #4 The final event status will be 'issuance_successful', indicating the issuance process has completed successfully. The issuance data is included with this event.
   *
   * @param input IssuanceRequestInput containing the necessary parameters for issuance.
   * @param onIssuanceEvent Callback function that will be invoked for issuance events or errors.
   * @param options Options to use polling (HTTP) instead of subscriptions (WebSockets). Note that the issuance event for `issuance_retrieved` is not returned when using polling.
   * @returns IssuanceResponse data containing the request ID, expiry, QR code and URL which must be opened on the mobile device by Microsoft Authenticator.
   * @throws An error if the issuance request creation fails. Subsequent errors will be returned via the onIssuanceEvent callback.
   */
  async createIssuanceRequest(
    { contractId, identityId, claims, pin, callback, expirationDate, faceCheckPhoto, photoCaptureRequestId }: IssuanceRequestInput,
    onIssuanceEvent: (event: IssuanceEventData | Error) => void,
    options?: { eventTimeout?: number; usePolling?: boolean; pollWaitInterval?: number },
  ): Promise<IssuanceResponse> {
    const request = {
      contractId,
      identityId,
      claims,
      pin: pin ? { length: pin.length, value: pin } : undefined,
      includeQRCode: true,
      callback: callback ? { url: callback.url, state: callback.state, headers: callback.headers } : undefined,
      expirationDate: expirationDate?.toISOString(),
      faceCheckPhoto,
      photoCaptureRequestId,
    }

    const response = await this.graphql<
      { request: typeof request },
      { createIssuanceRequest: IssuanceResponse | { error: { code: string; message: string } } }
    >(createIssuanceRequestMutation, {
      request,
    })

    const result = response.data.createIssuanceRequest
    if ('error' in result) throw new Error(`${result.error.code}: ${result.error.message}`)

    if (options?.usePolling) this.pollIssuanceEvent(result.requestId, onIssuanceEvent, options?.eventTimeout, options?.pollWaitInterval)
    else this.subscribeToIssuanceEvent(result.requestId, onIssuanceEvent, options?.eventTimeout)

    return result
  }

  private async subscribeToIssuanceEvent(
    requestId: string,
    onIssuanceEvent: (event: IssuanceEventData | Error) => void,
    eventTimeout: number = defaultEventTimeout,
  ) {
    const subscription = this.wsClient.iterate<{ issuanceEvent: IssuanceEventData }>({
      query: issuanceEventSubscription,
      variables: { requestId },
    })

    const timeout = setTimeout(() => {
      subscription.return?.()
      onIssuanceEvent(new Error('Issuance event subscription timed out'))
    }, eventTimeout)

    for await (const result of subscription) {
      if (result.errors) {
        clearTimeout(timeout)
        onIssuanceEvent(new Error(result.errors.map(({ message }) => message).join('\n')))
        break
      }
      if (!result.data) {
        clearTimeout(timeout)
        onIssuanceEvent(new Error('No issuance event data received'))
        break
      }

      onIssuanceEvent(result.data.issuanceEvent)

      const expectFurtherEvents = result.data.issuanceEvent.event.requestStatus === IssuanceRequestStatus.RequestRetrieved
      if (!expectFurtherEvents) {
        clearTimeout(timeout)
        break
      }
    }
  }

  private async pollIssuanceEvent(
    requestId: string,
    onIssuanceEvent: (event: IssuanceEventData | Error) => void,
    eventTimeout: number = defaultEventTimeout,
    pollWaitInterval = defaultPollWaitInterval,
  ) {
    const tryFindIssuance = async () => {
      const response = await this.graphql<{ requestId: string }, { findIssuances: Issuance[] }>(findIssuanceQuery, { requestId })
      if (!response.data.findIssuances.length) return false
      onIssuanceEvent({
        event: {
          requestId,
          requestStatus: IssuanceRequestStatus.IssuanceSuccessful,
        },
        issuance: response.data.findIssuances[0],
      })
      return true
    }

    let pollWaitRef: ReturnType<typeof setTimeout> | undefined

    const timeoutRef = setTimeout(() => {
      clearTimeout(pollWaitRef)
      onIssuanceEvent(new Error('Issuance event polling timed out'))
    }, eventTimeout)

    const poll = async (): Promise<void> => {
      try {
        const found = await tryFindIssuance()
        if (found) {
          clearTimeout(timeoutRef)
          if (pollWaitRef) clearTimeout(pollWaitRef)
          pollWaitRef = undefined
          return
        }
        pollWaitRef = setTimeout(() => {
          void poll()
        }, pollWaitInterval)
      } catch (error) {
        clearTimeout(timeoutRef)
        if (pollWaitRef) clearTimeout(pollWaitRef)
        pollWaitRef = undefined
        onIssuanceEvent(error instanceof Error ? error : new Error(String(error)))
      }
    }
    void poll()
  }

  /**
   * Initiates a presentation request using the supplied input.
   * Returns a presentation request response (or throws an error).
   * Presentation events are received via the onPresentationEvent callback.
   *
   * Presentation has the following steps:
   * #1 A presentation request is created and returned, it contains a QR code and URL, plus the request ID and expiry.
   * #2 For mobile apps, the URL can opened immediately to initiate the presentation process in Microsoft Authenticator. For web apps, the QR code should be displayed to be scanned via a mobile device to initiate presentation in Microsoft Authenticator.
   * #3 The onPresentationEvent callback will receive presentation events (or an Error). The first event status will be 'presentation_retrieved' when the request has been opened by Microsoft Authenticator.
   * #4 The final event status will be 'presentation_verified', indicating the presentation process has completed successfully. The presentation data is included with this event.
   *
   * @param input PresentationRequestInput containing the necessary parameters for presentation.
   * @param onPresentationEvent Callback function that will be invoked for presentation events or errors.
   * @param options Options to use polling (HTTP) instead of subscriptions (WebSockets). Note that the presentation event for `presentation_retrieved` is not returned when using polling.
   * @returns PresentationResponse data containing the request ID, expiry, QR code and URL which must be opened on the mobile device by Microsoft Authenticator.
   * @throws An error if the presentation request creation fails. Subsequent errors will be returned via the onPresentationEvent callback.
   */
  async createPresentationRequest(
    { callback, clientName, requestedCredentials, identityId }: PresentationRequestInput,
    onPresentationEvent: (event: PresentationEventData | Error) => void,
    options?: { eventTimeout?: number; usePolling?: boolean; pollWaitInterval?: number },
  ): Promise<PresentationResponse> {
    const request = {
      identityId,
      requestedCredentials,
      registration: { clientName },
      includeQRCode: true,
      callback: callback ? { url: callback.url, state: callback.state, headers: callback.headers } : undefined,
    }

    const response = await this.graphql<
      { request: typeof request },
      { createPresentationRequest: PresentationResponse | { error: { code: string; message: string } } }
    >(createPresentationRequestMutation, {
      request,
    })

    const result = response.data.createPresentationRequest
    if ('error' in result) throw new Error(`${result.error.code}: ${result.error.message}`)

    if (options?.usePolling)
      this.pollPresentationEvent(result.requestId, onPresentationEvent, options?.eventTimeout, options?.pollWaitInterval)
    else this.subscribeToPresentationEvent(result.requestId, onPresentationEvent, options?.eventTimeout)

    return result
  }

  /**
   * Initiates a presentation request for authorization.
   * For use by the OIDC provider login UI only, not for general use.
   */
  async createPresentationRequestForAuthn(
    onPresentationEvent: (event: PresentationEventData | Error) => void,
    options?: { eventTimeout?: number; usePolling?: boolean; pollWaitInterval?: number },
  ): Promise<PresentationResponse> {
    const response = await this.graphql<
      Record<string, never>,
      { createPresentationRequestForAuthn: PresentationResponse | { error: { code: string; message: string } } }
    >(createPresentationRequestForAuthnMutation, {})

    const result = response.data.createPresentationRequestForAuthn
    if ('error' in result) throw new Error(`${result.error.code}: ${result.error.message}`)

    if (options?.usePolling)
      this.pollPresentationEvent(result.requestId, onPresentationEvent, options?.eventTimeout, options?.pollWaitInterval)
    else this.subscribeToPresentationEvent(result.requestId, onPresentationEvent, options?.eventTimeout)

    return result
  }

  private async subscribeToPresentationEvent(
    requestId: string,
    onPresentationEvent: (event: PresentationEventData | Error) => void,
    eventTimeout: number = defaultEventTimeout,
  ) {
    const subscription = this.wsClient.iterate<{ presentationEvent: PresentationEventData }>({
      query: presentationEventSubscription,
      variables: { requestId },
    })

    const timeout = setTimeout(() => {
      subscription.return?.()
      onPresentationEvent(new Error('Presentation event subscription timed out'))
    }, eventTimeout)

    for await (const result of subscription) {
      if (result.errors) {
        clearTimeout(timeout)
        onPresentationEvent(new Error(result.errors.map(({ message }) => message).join('\n')))
        break
      }
      if (!result.data) {
        clearTimeout(timeout)
        onPresentationEvent(new Error('No presentation event data received'))
        break
      }

      onPresentationEvent(result.data.presentationEvent)

      const expectFurtherEvents = result.data.presentationEvent.event.requestStatus === PresentationRequestStatus.RequestRetrieved
      if (!expectFurtherEvents) {
        clearTimeout(timeout)
        break
      }
    }
  }

  private async pollPresentationEvent(
    requestId: string,
    onPresentationEvent: (event: PresentationEventData | Error) => void,
    eventTimeout: number = defaultEventTimeout,
    pollWaitInterval = defaultPollWaitInterval,
  ) {
    const tryFindPresentation = async () => {
      const response = await this.graphql<{ requestId: string }, { findPresentations: Presentation[] }>(findPresentationQuery, {
        requestId,
      })
      if (!response.data.findPresentations.length) return false
      onPresentationEvent({
        event: {
          requestId,
          requestStatus: PresentationRequestStatus.PresentationVerified,
        },
        presentation: response.data.findPresentations[0],
      })
      return true
    }

    let pollWaitRef: ReturnType<typeof setTimeout> | undefined

    const timeoutRef = setTimeout(() => {
      clearTimeout(pollWaitRef)
      onPresentationEvent(new Error('Presentation event polling timed out'))
    }, eventTimeout)

    const poll = async (): Promise<void> => {
      try {
        const found = await tryFindPresentation()
        if (found) {
          clearTimeout(timeoutRef)
          if (pollWaitRef) clearTimeout(pollWaitRef)
          pollWaitRef = undefined
          return
        }
        pollWaitRef = setTimeout(() => {
          void poll()
        }, pollWaitInterval)
      } catch (error) {
        clearTimeout(timeoutRef)
        if (pollWaitRef) clearTimeout(pollWaitRef)
        pollWaitRef = undefined
        onPresentationEvent(error instanceof Error ? error : new Error(String(error)))
      }
    }
    void poll()
  }
  /**
   * Initiates a photo capture request, allowing a user to capture a photo to be used in a subsequent credential issuance.
   * Note that `createPhotoCaptureIssuance` runs both photo capture and issuance operations and is generally recommended for issuances requiring photo capture.
   *
   * Photo capture has the following steps:
   * #1 A photo capture request is created and returned, it contains a QR code and URL, plus the request ID.
   * #2 The URL or QR code can be opened in whatever device is being used to capture the photo.
   * #3 The onPhotoCaptureEvent callback will receive photo capture events. The first event status will be 'started' when the photo capture URL has been opened.
   * #4 The final event will be 'complete', indicating the photo has been captured successfully and is ready to use in a subsequent issuance.
   *
   * @param request PhotoCaptureRequestInput containing the necessary parameters for the photo capture request.
   * @param onPhotoCaptureEvent Callback function that will be invoked for photo capture events (or errors).
   * @param options Options to use polling (HTTP) instead of subscriptions (WebSockets).
   * @returns PhotoCaptureResponse data containing the request ID, expiry, QR code and URL which can be opened on whatever device is being used to capture the photo.
   * @throws An error if the presentation request creation fails. Subsequent errors will be returned via the onPhotoCaptureEvent callback.
   */
  async createPhotoCaptureRequest(
    request: PhotoCaptureRequestInput,
    onPhotoCaptureEvent: (event: PhotoCaptureEventData | Error) => void,
    options?: { eventTimeout?: number; usePolling?: boolean; pollWaitInterval?: number },
  ): Promise<PhotoCaptureResponse> {
    const response = await this.graphql<
      { request: PhotoCaptureRequestInput },
      { createPhotoCaptureRequest: PhotoCaptureResponse | { error: { code: string; message: string } } }
    >(createPhotoCaptureRequestMutation, {
      request,
    })
    const result = response.data.createPhotoCaptureRequest
    if ('error' in result) throw new Error(`${result.error.code}: ${result.error.message}`)

    if (options?.usePolling) this.pollPhotoCaptureEvent(result.id, onPhotoCaptureEvent, options?.eventTimeout, options?.pollWaitInterval)
    else this.subscribeToPhotoCaptureEvent(result.id, onPhotoCaptureEvent, options?.eventTimeout)

    return result
  }

  private async subscribeToPhotoCaptureEvent(
    photoCaptureRequestId: string,
    onPhotoCaptureEvent: (event: PhotoCaptureEventData | Error) => void,
    eventTimeout: number = defaultEventTimeout,
  ) {
    const subscription = this.wsClient.iterate<{ photoCaptureEvent: PhotoCaptureEventData }>({
      query: photoCaptureEventSubscription,
      variables: { photoCaptureRequestId },
    })

    const timeout = setTimeout(() => {
      subscription.return?.()
      onPhotoCaptureEvent(new Error('Photo capture event subscription timed out'))
    }, eventTimeout)

    for await (const result of subscription) {
      if (result.errors) {
        clearTimeout(timeout)
        onPhotoCaptureEvent(new Error(result.errors.map(({ message }) => message).join('\n')))
        break
      }
      if (!result.data) {
        clearTimeout(timeout)
        onPhotoCaptureEvent(new Error('No photo capture event data received'))
        break
      }

      onPhotoCaptureEvent(result.data.photoCaptureEvent)

      const expectFurtherEvents =
        result.data.photoCaptureEvent.status === PhotoCaptureStatus.NotStarted ||
        result.data.photoCaptureEvent.status === PhotoCaptureStatus.Started
      if (!expectFurtherEvents) {
        clearTimeout(timeout)
        break
      }
    }
  }

  private async pollPhotoCaptureEvent(
    photoCaptureRequestId: string,
    onPhotoCaptureEvent: (event: PhotoCaptureEventData | Error) => void,
    eventTimeout: number = defaultEventTimeout,
    pollWaitInterval = defaultPollWaitInterval,
  ) {
    const tryFindPhotoCaptureStatus = async () => {
      const response = await this.graphql<{ photoCaptureRequestId: string }, { photoCaptureStatus: PhotoCaptureEventData }>(
        photoCaptureStatusQuery,
        {
          photoCaptureRequestId,
        },
      )
      if (!response.data.photoCaptureStatus) return false
      onPhotoCaptureEvent(response.data.photoCaptureStatus)
      return response.data.photoCaptureStatus.status === PhotoCaptureStatus.Complete
    }

    let pollWaitRef: ReturnType<typeof setTimeout> | undefined

    const timeoutRef = setTimeout(() => {
      clearTimeout(pollWaitRef)
      onPhotoCaptureEvent(new Error('Photo capture event polling timed out'))
    }, eventTimeout)

    const poll = async (): Promise<void> => {
      try {
        const found = await tryFindPhotoCaptureStatus()
        if (found) {
          clearTimeout(timeoutRef)
          if (pollWaitRef) clearTimeout(pollWaitRef)
          pollWaitRef = undefined
          return
        }
        pollWaitRef = setTimeout(() => {
          void poll()
        }, pollWaitInterval)
      } catch (error) {
        clearTimeout(timeoutRef)
        if (pollWaitRef) clearTimeout(pollWaitRef)
        pollWaitRef = undefined
        onPhotoCaptureEvent(error instanceof Error ? error : new Error(String(error)))
      }
    }
    void poll()
  }

  /**
   * Runs a photo capture request followed by an issuance request using the captured photo.
   * This function can be used for issuances that require a photo to be captured by the user.
   * The combined process has both photo capture and issuance steps.
   *
   * Photo capture steps:
   * #1 A photo capture request is created and returned, it contains a QR code and URL, plus the request ID.
   * #2 The URL or QR code can be opened in whatever device is being used to capture the photo.
   * #3 The onPhotoCaptureEvent callback will receive photo capture events. The first event status will be 'started' when the photo capture URL has been opened.
   * #4 The final event will be 'complete', indicating the photo has been captured successfully and is ready to use in a subsequent issuance.
   * #5 Issuance steps will then be initiated...
   *
   * Issuance steps:
   * #1 An issuance request is created and returned via the onIssuance callback function, it contains a QR code and URL, plus the request ID and expiry.
   * #2 For mobile apps, the URL can opened immediately to initiate the issuance process in Microsoft Authenticator. For web apps, the QR code should be displayed to be scanned via a mobile device to initiate issuance in Microsoft Authenticator.
   * #3 The onIssuanceEvent callback will receive issuance events (or an Error). The first event status will be 'issuance_retrieved' when the request has been opened by Microsoft Authenticator. If a PIN code has been specified in the issuance request, it must be entered by the user at this point.
   * #4 The final event status will be 'issuance_successful', indicating the issuance process has completed successfully. The issuance data is included with this event.
   *
   * @param input IssuanceRequestInput without faceCheckPhoto or photoCaptureRequestId.
   * @param onPhotoCaptureEvent Callback function that will be invoked for photo capture events.
   * @param onIssuance Callback function that will be invoked with the issuance request response (QR code and URL).
   * @param onIssuanceEvent Callback function that will be invoked for issuance events.
   * @param options Options to use polling (HTTP) instead of subscriptions (WebSockets). Note that the issuance event for `issuance_retrieved` is not returned when using polling.
   * @returns PhotoCaptureResponse data containing the request ID, expiry, QR code and URL which can be opened on whatever device is being used to capture the photo.
   * @throws An error if the presentation request creation fails. Subsequent errors will be returned via the onPhotoCaptureEvent, onIssuance and onIssuanceEvent callback functions.
   */
  async createPhotoCaptureIssuance(
    input: Omit<IssuanceRequestInput, 'faceCheckPhoto' | 'photoCaptureRequestId'>,
    onPhotoCaptureEvent: (event: PhotoCaptureEventData | Error) => void,
    onIssuance: (response: IssuanceResponse) => void,
    onIssuanceEvent: (event: IssuanceEventData | Error) => void,
    options?: { eventTimeout?: number; usePolling?: boolean; pollWaitInterval?: number },
  ): Promise<PhotoCaptureResponse> {
    const { contractId, identityId, claims, pin, callback, expirationDate } = input

    let photoCaptureRequestId: string

    const handlePhotoCaptureEvent = async (event: PhotoCaptureEventData | Error) => {
      onPhotoCaptureEvent(event)

      if ('status' in event && event.status === PhotoCaptureStatus.Complete) {
        this.createIssuanceRequest(
          { contractId, identityId, claims, pin, callback, expirationDate, photoCaptureRequestId },
          onIssuanceEvent,
          options,
        )
          .then(onIssuance)
          .catch(onIssuanceEvent)
      }
    }

    return this.createPhotoCaptureRequest({ contractId, identityId }, handlePhotoCaptureEvent, options).then((response) => {
      photoCaptureRequestId = response.id
      return response
    })
  }

  private async graphql<TVariables, TResult>(query: string, variables: TVariables) {
    const response = await fetch(this.url, {
      method: 'POST',
      body: JSON.stringify({ query, variables }),
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.accessToken}` },
    })
    if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`)
    const json = await response.json()
    if (json.errors) throw new Error(json.errors.map((error: Error) => `${JSON.stringify(error.message)}`).join('\n'))
    return json as { data: TResult }
  }
}
